import { inject } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRole } from '../models/user.model';
import { map, switchMap, take } from 'rxjs/operators';

/**
 * Route guard that restricts access based on user role.
 * Usage in route config:
 *   canActivate: [roleGuard],
 *   data: { allowedRoles: ['operator', 'approver'] }
 */
export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const allowedRoles: UserRole[] = route.data?.['allowedRoles'];

  // 1. Fast path: If signal is already populated (normal in-app navigation)
  const currentUser = authService.getCurrentUser();
  if (currentUser) {
    if (allowedRoles && allowedRoles.includes(currentUser.role)) {
      return true;
    }
    router.navigate(['/dhamnagar-dashboard/dashboard']);
    return false;
  }

  // 2. Slow path: Hard refresh. Signal is not yet populated.
  // We must wait for Firebase Auth to initialize before making a decision.
  return authService.authState$.pipe(
    take(1),
    map(firebaseUser => {
      if (!firebaseUser) {
        router.navigate(['/login']);
        return false;
      }

      // Auth is valid, but full user profile is still loading in AuthService.
      // We use the localStorage cache to determine role immediately so routing isn't blocked.
      const rawCached = localStorage.getItem(`ppms_role_${firebaseUser.uid}`);
      // Normalize legacy 'minister' → 'osd' so allowedRoles checks work consistently.
      const cachedRole = (rawCached === 'minister' ? 'osd' : rawCached) as UserRole | null;

      // If we have a cached role, check it
      if (cachedRole) {
        if (allowedRoles && allowedRoles.includes(cachedRole)) {
          return true;
        }
      } else {
        // Fallback role inference if no cache exists (first ever login on this device might hit this on a rapid refresh)
        const email = (firebaseUser.email || '').toLowerCase();
        let fallbackRole: UserRole = UserRole.OPERATOR;
        if (email.includes('osd') || email.includes('minister')) fallbackRole = UserRole.OSD;
        else if (email.includes('admin'))                        fallbackRole = UserRole.ADMIN;
        else if (email.includes('approver'))                     fallbackRole = UserRole.APPROVER;

        if (allowedRoles && allowedRoles.includes(fallbackRole)) {
          return true;
        }
      }

      // Redirect to default dashboard if role mismatch
      router.navigate(['/dhamnagar-dashboard/dashboard']);
      return false;
    })
  );
};
